import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Eye, EyeOff, Edit, X, Upload, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { BLOG_CATEGORIES } from "@/lib/data";

const input = "w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#C9A84C]";
const EMPTY = { title: "", category: "cuisine", excerpt: "", content: "", cover_url: "", published: false };

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => { api.get("/blog", { params: { include_hidden: true } }).then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ title: p.title, category: p.category, excerpt: p.excerpt, content: p.content, cover_url: p.cover_url || "", published: p.published }); setEditing(p); };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((f) => ({ ...f, cover_url: r.data.url })); }
    catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    try {
      if (editing === "new") await api.post("/blog", form);
      else await api.put(`/blog/${editing.id}`, form);
      toast.success("Article enregistré");
      setEditing(null); load();
    } catch { toast.error("Échec de l'enregistrement"); }
  };

  const update = async (p, patch) => { try { await api.put(`/blog/${p.id}`, { ...p, ...patch }); load(); } catch { toast.error("Échec"); } };
  const remove = async (id) => { if (!window.confirm("Supprimer cet article ?")) return; try { await api.delete(`/blog/${id}`); load(); } catch { toast.error("Échec"); } };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[#2C2C2C]">Le Journal Afro Regal</h1>
        <button onClick={openNew} data-testid="blog-new" className="flex items-center gap-2 rounded-lg bg-[#2C2C2C] px-5 py-2.5 text-sm text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C]"><Plus size={15} /> Nouvel article</button>
      </div>

      <div className="mt-6 space-y-3">
        {items.length === 0 && <p className="text-[#2C2C2C]/50" data-testid="blog-empty-admin">Aucun article.</p>}
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 rounded-xl border border-[#2C2C2C]/10 bg-white p-4" data-testid={`blog-item-${p.id}`}>
            <div>
              <p className="font-medium text-[#2C2C2C]">{p.title} {p.published ? <span className="ml-2 text-xs text-green-700">publié</span> : <span className="ml-2 text-xs text-[#2C2C2C]/40">brouillon</span>}</p>
              <p className="text-xs text-[#2C2C2C]/50">{BLOG_CATEGORIES.find((c) => c.value === p.category)?.label}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => update(p, { published: !p.published })} className="text-[#2C2C2C]/70">{p.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
              <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className="text-[#2C2C2C]/70"><Edit size={16} /></button>
              <button onClick={() => remove(p.id)} className="text-red-600"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" onClick={() => setEditing(null)}>
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-[#FAF6EF] p-7 thin-scroll" onClick={(e) => e.stopPropagation()} data-testid="blog-editor">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-[#2C2C2C]">{editing === "new" ? "Nouvel article" : "Modifier l'article"}</h2>
              <button onClick={() => setEditing(null)} className="text-[#2C2C2C]/50"><X size={22} /></button>
            </div>
            <div className="mt-5 space-y-4">
              <input className={input} placeholder="Titre" data-testid="blog-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {BLOG_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <textarea className={input} rows={2} placeholder="Extrait / résumé" data-testid="blog-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              <textarea className={input} rows={10} placeholder="Contenu de l'article" data-testid="blog-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
              <div>
                <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Image de couverture</label>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#2C2C2C]/30 bg-white px-4 py-3 text-sm text-[#2C2C2C]/70 hover:border-[#C9A84C]">
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Téléverser une image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
                </label>
                {form.cover_url && <img src={mediaUrl(form.cover_url)} alt="couverture" className="mt-3 h-32 rounded-lg object-cover" />}
              </div>
              <label className="flex items-center gap-2 text-sm text-[#2C2C2C]/80">
                <input type="checkbox" className="h-4 w-4 accent-[#2C2C2C]" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-published" />
                Publier immédiatement
              </label>
              <button onClick={save} data-testid="blog-save" className="w-full rounded-lg bg-[#2C2C2C] py-3 text-sm text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C]">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
